import React from 'react';
import { FieldAttributes, useField } from 'formik';
import { FormFieldTooltip, TooltipIconData } from './FormFieldTooltip';
import classNames from 'classnames';

const Input = React.forwardRef<
    HTMLInputElement,
    React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> &
        FieldAttributes<any> & {
            currencySymbol?: string;
            tooltip?: React.ReactNode;
            tooltipIconData?: TooltipIconData | null;
        } & {
            disableFormikBlur?: boolean;
            numbersOnly?: boolean;
        }
>(
    (
        {
            className = '',
            onChange,
            currencySymbol,
            tooltip,
            tooltipIconData,
            disableFormikBlur = false,
            numbersOnly = false,
            ...props
        },
        ref
    ) => {
        const [field, meta] = useField({ name: props.name });
        const fieldValue = field.value == null ? '' : field.value;

        const showError = meta.touched && meta.error;

        const onBlur = (e: Event) => {
            props.onBlur && props.onBlur(e);
            !disableFormikBlur && field.onBlur(e);
        };

        if (props.type === 'textarea') {
            return (
                <div className={`InputWrapper FormTextArea${currencySymbol ? 'HasSymbol' : ''}`}>
                    {tooltip && (
                        <FormFieldTooltip tooltip={tooltip} tooltipIconData={tooltipIconData} />
                    )}
                    <textarea
                        rows={8}
                        maxLength={1000}
                        {...(field as any)}
                        value={fieldValue}
                        className={classNames('EditBox', className, {
                            ErrorInput: showError,
                            WithTooltip: tooltip,
                        })}
                        {...props}
                        onBlur={onBlur}
                        ref={ref}
                    ></textarea>
                </div>
            );
        }
        return (
            <div className={`InputWrapper ${currencySymbol ? 'HasSymbol' : ''}`}>
                {tooltip && (
                    <FormFieldTooltip tooltip={tooltip} tooltipIconData={tooltipIconData} />
                )}
                <input
                    {...(field as any)}
                    value={fieldValue}
                    className={classNames('EditBox', className, {
                        ErrorInput: showError,
                        WithTooltip: tooltip,
                    })}
                    disabled={props.disabled === true}
                    {...props}
                    onBlur={onBlur}
                    ref={ref}
                    onChange={(e) => {
                        if (numbersOnly) {
                            const inputEl = e.target as HTMLInputElement;
                            const filtered = inputEl.value.replace(/[^0-9]/g, '');
                            inputEl.value = filtered;
                        }
                        field.onChange(e);
                        onChange && onChange(e);
                    }}
                    onWheel={(e) => {
                        // Scrolling the page while a number input is focused would otherwise
                        // silently increment/decrement its value (native browser behavior).
                        if (props.type === 'number') {
                            (e.target as HTMLInputElement).blur();
                        }
                    }}
                />
                {currencySymbol && <span className="CurrencySymbol">{currencySymbol}</span>}
            </div>
        );
    }
);

export { Input };
